import React from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { v4 as uuidv4 } from "uuid";
import { Section } from "../Section";
import { LanguageToggle } from "../LanguageToggle";
import { ColumnSection } from "./ColumnSection";
import { Icon } from "../Icons";
import * as styles from "./Footer.module.scss";

const defaultLabels = {
	selectLanguage: "Language preference",
	socialAppLinksTitle: "Download the App and follow us.",
};

const Footer = ({
	className,
	columns,
	copyrightText,
	altLangPages,
	locale,
	labels: providedLabels,
	renderLanguageToggle,
	renderLinks,
	renderImage,
	logoData,
	socialLinks,
	appStoreLinks,
}) => {
	const labels = { ...defaultLabels, ...providedLabels };

	return (
		<Section as="footer" className={`${className} ${styles.footer}`}>
			{(!!columns?.length || logoData?.logoImage || logoData?.logoMessage) && (
				<div
					className={cx(
						styles.footer__body,
						!logoData?.logoImage && !logoData?.logoMessage && styles.noLogo
					)}>
					{!!columns?.length && (
						<div className={styles.footer__sections}>
							{columns?.map(column => {
								if (Array.isArray(column)) {
									return (
										<div key={uuidv4()} className={styles.footer__section__container}>
											{column.map(columnItem => (
												<ColumnSection
													key={uuidv4()}
													keyId={columnItem.id}
													renderLinks={renderLinks}
													columnData={columnItem}
												/>
											))}
										</div>
									);
								}

								return (
									<div key={uuidv4()} className={styles.footer__section__container}>
										<ColumnSection
											keyId={column.id}
											renderLinks={renderLinks}
											columnData={column}
										/>
									</div>
								);
							})}
						</div>
					)}
					<div className={styles.footer__logoContainer}>
						{(logoData?.logoImage || logoData?.logoMessage) && (
							<div className={styles.footer__logo}>
								{logoData?.logoImage && (
									<div className={styles.footer__corporateLogo}>
										{renderImage(logoData.logoImage, {
											className: styles.logoImage,
										})}
									</div>
								)}
								{logoData?.logoMessage && (
									<div className={styles.footer__corporateMessage}>{logoData.logoMessage}</div>
								)}
							</div>
						)}
						{(socialLinks?.length || appStoreLinks?.length) && (
							<div className={styles.footer__socialLinksContainer}>
								{labels.socialAppLinksTitle && (
									<div className={styles.footer__linksTitle}>{labels.socialAppLinksTitle}</div>
								)}
								<div className={styles.footer__socialLinksContainer__links}>
									{socialLinks.map(link =>
										renderLinks(
											{
												...link,
												noStyle: true,
												showExternalIcon: false,
												title: <Icon title={link.linkText} name={link.iconName} />,
											},
											styles.footer__instagramIconLink
										)
									)}
									{appStoreLinks?.length > 0 &&
										appStoreLinks.map(link =>
											renderLinks(
												{
													...link,
													noStyle: true,
													showExternalIcon: false,
													title:
														!!link.image?.[0]?.secure_url &&
														renderImage(
															{
																src: link.image[0].secure_url,
																alt: link.imageAltText,
															},
															{ className: styles.footer__appStoreIcon }
														),
												},
												styles.footer__appStoreLink
											)
										)}
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			<div className={styles.footer__bottom}>
				{copyrightText && (
					<div className={styles.footer__copyright}>
						<p>{copyrightText}</p>
					</div>
				)}
				{renderLanguageToggle && (
					<div className={styles.footer__lang}>
						{renderLanguageToggle(altLangPages, {
							classNames: {
								currentLanguage: styles.langToggle__current,
								altLanguage: styles.langToggle__alternative,
							},
							locale,
							labels: {
								selectLanguage: labels.selectLanguage,
							},
						})}
					</div>
				)}
			</div>
		</Section>
	);
};

Footer.propTypes = {
	className: PropTypes.string,
	altLangPages: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			path: PropTypes.string.isRequired,
			lang: PropTypes.string.isRequired,
		})
	),
	locale: PropTypes.string,
	labels: PropTypes.shape({
		selectLanguage: PropTypes.string,
		socialAppLinksTitle: PropTypes.string,
	}),
	columns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.array])),
	copyrightText: PropTypes.string.isRequired,
	renderLanguageToggle: PropTypes.func,
	renderLinks: PropTypes.func,
	renderImage: PropTypes.func,
	logoData: PropTypes.shape({
		logoMessage: PropTypes.string,
		logoImage: PropTypes.oneOf([
			PropTypes.any,
			PropTypes.shape({
				src: PropTypes.string,
				alt: PropTypes.string,
			}),
		]),
	}).isRequired,
	socialLinks: PropTypes.arrayOf(PropTypes.object),
	appStoreLinks: PropTypes.arrayOf(PropTypes.object),
};

Footer.defaultProps = {
	className: undefined,
	altLangPages: undefined,
	locale: undefined,
	labels: defaultLabels,
	renderLinks: (link, className) => (
		<a className={className} href={link.url} target={link.target}>
			{link.title}
		</a>
	),
	renderImage: (image, { className }) => (
		<img className={className} src={image.src} alt={image.alt} />
	),
	renderLanguageToggle: (altLangPages, { classNames, locale, labels }) => (
		<LanguageToggle
			altLangPages={altLangPages}
			classNames={classNames}
			locale={locale}
			variant="drop-down"
			labels={labels}
		/>
	),
	columns: undefined,
	socialLinks: undefined,
	appStoreLinks: undefined,
};

export { Footer };
